// QCARE-AI-DEV-FRONT-DARK-CARDS-V2 (2026-05-09) — single consolidated
// trust bar for the dark Hero/Budget cards.
//
// Replaces the prior duplication where the dark cards rendered both:
//   • TrustBadge ("منتج أصلي 100%") in the header row
//   • inline emerald "أصلي 100% — معتمد رسمياً" line in the body
// → reads as duplicated certificate copy.
//
// New shape: ONE full-width pill at the bottom of the card text column:
//   🔒 أصلي 100% · مورد معتمد · عرض الشهادة
//
// "عرض الشهادة" is the visible affordance — tapping anywhere on the bar
// opens the same TrustModal that TrustBadge does. The button swallows
// click propagation so the wrapping <Link> doesn't navigate away.
'use client';

import { useState } from 'react';
import { TrustModal } from '@/components/TrustModal';

export function TrustBar({ size = 'lg' }: { size?: 'lg' | 'sm' }) {
    const [open, setOpen] = useState(false);

    // QCARE-AI-DEV-FRONT-TRUSTBAR-GREEN (2026-05-09): palette swap from
    // cyan/teal to emerald/green per the reference design. The bar is
    // already full-width (`width: 100%`) so it stretches across the
    // product card's text-column inner width on both Hero and Budget
    // cards. The two `size` variants (`'lg'` for Hero, `'sm'` for
    // Budget) tune padding + fontSize + gap; everything else (palette,
    // hover, "عرض الشهادة" underline + click target opening TrustModal)
    // is shared.
    const padding = size === 'sm' ? '8px 12px' : '10px 16px';
    const fontSize = size === 'sm' ? 11 : 12;
    const gap = size === 'sm' ? 8 : 12;
    const dotOpacity = 0.50;

    const BG_DEFAULT = 'linear-gradient(135deg, rgba(16, 185, 129, 0.13), rgba(52, 211, 153, 0.07))';
    const BG_HOVER = 'linear-gradient(135deg, rgba(16, 185, 129, 0.20), rgba(52, 211, 153, 0.12))';
    const BORDER_DEFAULT = 'rgba(52, 211, 153, 0.48)';
    const BORDER_HOVER = 'rgba(52, 211, 153, 0.65)';
    const TEXT_GREEN = '#34D399';
    const SHADOW = '0 0 18px rgba(16, 185, 129, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.06)';

    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(true);
                }}
                title="اضغط لعرض شهادة الأصالة"
                aria-label="منتج أصلي 100% · مورد معتمد · اضغط لعرض شهادة الأصالة"
                aria-haspopup="dialog"
                aria-expanded={open}
                style={{
                    background: BG_DEFAULT,
                    border: `1px solid ${BORDER_DEFAULT}`,
                    borderRadius: 999,
                    padding,
                    color: TEXT_GREEN,
                    width: '100%',
                    fontWeight: 600,
                    fontSize,
                    direction: 'rtl',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap,
                    cursor: 'pointer',
                    transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                    fontFamily: 'inherit',
                    boxShadow: SHADOW,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = BG_HOVER;
                    e.currentTarget.style.borderColor = BORDER_HOVER;
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = BG_DEFAULT;
                    e.currentTarget.style.borderColor = BORDER_DEFAULT;
                }}
            >
                <span aria-hidden>🔒</span>
                <span>أصلي 100%</span>
                <span aria-hidden style={{ opacity: dotOpacity }}>·</span>
                <span>مورد معتمد</span>
                <span aria-hidden style={{ opacity: dotOpacity }}>·</span>
                <span style={{ textDecoration: 'underline', textUnderlineOffset: 4 }}>عرض الشهادة</span>
            </button>
            {open && <TrustModal onClose={() => setOpen(false)} />}
        </>
    );
}
